import React from 'react';
import { Play, Pause, Square } from 'lucide-react';
import CircularTherapyTimer from '@/components/CircularTherapyTimer';
import CoachTherapyBanner from '@/components/CoachTherapyBanner';
import PremiumButton from '@/components/PremiumButton';
import PremiumHeader from '@/components/PremiumHeader';
import { useTherapyTimer } from '@/hooks/useTherapyTimer';

/**
 * TherapyTimerDemo - Page de démonstration du système de timer thérapeutique
 * Pour tester avant intégration dans les vrais players
 */
const TherapyTimerDemo = ({ t }) => {
  const {
    todaySeconds,
    sessionSeconds,
    isPlaying,
    goalCompleted,
    goalSeconds,
    start,
    pause,
    stop,
    percentage
  } = useTherapyTimer('demo', 12); // 12 minutes goal

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#151A24] via-[#1D2333] to-[#151A24] pb-32">
      <PremiumHeader
        title="Therapy Timer Demo"
        subtitle="Test du système de suivi thérapeutique en temps réel"
        showBackButton={true}
      />

      <div className="max-w-2xl mx-auto px-4">
        {/* Stats Card */}
        <div className="bg-white/[0.06] backdrop-blur-xl border border-white/[0.08] rounded-2xl p-6 mb-6">
          <h3 className="text-white/90 font-semibold mb-4">Statistiques</h3>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-white/60">Aujourd'hui</span>
              <p className="text-white/90 text-2xl font-bold mt-1">
                {Math.floor(todaySeconds / 60)}:{(todaySeconds % 60).toString().padStart(2, '0')}
              </p>
            </div>
            <div>
              <span className="text-white/60">Session</span>
              <p className="text-white/90 text-2xl font-bold mt-1">
                {Math.floor(sessionSeconds / 60)}:{(sessionSeconds % 60).toString().padStart(2, '0')}
              </p>
            </div>
            <div>
              <span className="text-white/60">Progression</span>
              <p className="text-white/90 text-2xl font-bold mt-1">
                {Math.round(percentage)}%
              </p>
            </div>
            <div>
              <span className="text-white/60">Statut</span>
              <p className={`text-2xl font-bold mt-1 ${goalCompleted ? 'text-green-400' : 'text-purple-400'}`}>
                {goalCompleted ? '✓' : isPlaying ? '▶' : '⏸'}
              </p>
            </div>
          </div>
        </div>

        {/* Circular Timer */}
        <div className="bg-white/[0.06] backdrop-blur-xl border border-white/[0.08] rounded-2xl p-6 mb-6">
          <h3 className="text-white/90 font-semibold mb-4 text-center">Timer Circulaire Premium</h3>
          <CircularTherapyTimer
            current={todaySeconds}
            goal={goalSeconds}
            isActive={isPlaying}
          />
        </div>

        {/* Coach Banner */}
        <div className="mb-6">
          <h3 className="text-white/90 font-semibold mb-4 px-4">Bandeau Coach Dynamique</h3>
          <CoachTherapyBanner
            current={todaySeconds}
            goal={goalSeconds}
            isPlaying={isPlaying}
            language="fr"
          />
        </div>

        {/* Controls */}
        <div className="bg-white/[0.06] backdrop-blur-xl border border-white/[0.08] rounded-2xl p-6">
          <h3 className="text-white/90 font-semibold mb-4">Contrôles</h3>
          <div className="flex gap-3">
            {!isPlaying ? (
              <PremiumButton
                onClick={start}
                variant="primary"
                fullWidth
                icon={<Play className="w-5 h-5" />}
              >
                Démarrer
              </PremiumButton>
            ) : (
              <PremiumButton
                onClick={pause}
                variant="secondary"
                fullWidth
                icon={<Pause className="w-5 h-5" />}
              >
                Pause
              </PremiumButton>
            )}
            
            <PremiumButton
              onClick={stop}
              variant="ghost"
              icon={<Square className="w-5 h-5" />}
            >
              Arrêter
            </PremiumButton>
          </div>

          <div className="mt-4 text-xs text-white/50 text-center">
            Le timer simule l'écoute thérapeutique. Les données sont enregistrées en temps réel.
          </div>
        </div>

        {/* Info */}
        <div className="mt-6 bg-purple-500/10 border border-purple-500/20 rounded-2xl p-4">
          <p className="text-sm text-purple-300 leading-relaxed">
            💡 <strong>Info:</strong> Ce timer sera intégré dans les players Radio, Sound Therapy et Playlists. 
            Il suit automatiquement votre progression quotidienne et déclenche les messages Coach.
          </p>
        </div>
      </div>
    </div>
  );
};

export default TherapyTimerDemo;
